<template>
  <div class="table-tree__item" style="display: contents">
    <transition name="fade">
      <tr
        v-show="isOpen"
        class="table-tree__tr"
        :class="classListRow"
        @click="isCategory ? openChilds() : null"
      >
        <td
          v-for="(col, index) in columns"
          :key="index"
          :style="index === 0 ? styleFirstTd : null"
          class="table-tree__td"
        >
          <span
            v-if="isCategory && index === 0"
            class="table-tree__icon"
            :class="!isOpenChild ? 'table-tree__icon--opened' : ''"
          >
            <svgicon
              icon="button/rollup"
              width="13"
              height="9"
            />
          </span>

          <template v-if="$scopedSlots.cell">
            <slot
              name="cell"
              :item="item"
              :col="col"
              :lvl="lvl"
              :is-category="isCategory"
              :index="index"
            />
          </template>

          <template v-else>
            <span
              v-if="!col.isLink || item.isNotLink"
              :class="isCategory ? 'bold' : ''"
              v-html="(isCategory && index !== 0 && !item.isFillRow) ? '' : col.handlerFormat ? col.handlerFormat(item[col.name]) : item[col.name]"
            />
          </template>

          <TableLinkList
            v-if="!isCategory && col.isLink && Array.isArray(item[col.name])"
            :list="item[col.name]"
            :router-data="col.routerData"
          />
        </td>
        <td
          v-if="showActionColumn"
          class="table-tree__td table-tree__td--actions"
          @click.stop
        >
          <div :style="{ visibility: canShowActions ? 'visible' : 'hidden' }">
            <slot
              name="actionButton"
              :item="item"
              :lvl="lvl"
              :is-category="isCategory"
              :root-item="rootItem"
              :parent-item="parentItem"
            />
          </div>
        </td>
      </tr>
    </transition>

    <!-- Динамический рендеринг дочерних элементов -->
    <transition-group
      v-show="isOpen && isOpenChild"
      tag="div"
      style="display: contents"
    >
      <TableTreeItem
        v-for="(child, i) of currentLevelChildren"
        :key="`${child.key}-${i}`"
        :count-item="i + countItem + 1"
        :columns="columns"
        :item="child.item"
        :items-length="currentLevelChildren.length"
        :lvl="lvl + 1"
        :children-keys="childrenKeys"
        :is-open="isOpenChild"
        :visible-child="visibleChild"
        :show-action-column="showActionColumn"
        :show-actions-rule="showActionsRule"
        :root-item="rootItem || item"
        :parent-item="item"
      >
        <template v-if="$scopedSlots.cell" #cell="slotProps">
          <slot name="cell" v-bind="slotProps" />
        </template>
        <template v-if="$scopedSlots.actionButton" #actionButton="slotProps">
          <slot name="actionButton" v-bind="slotProps" />
        </template>
      </TableTreeItem>
    </transition-group>
  </div>
</template>

<script>
import TableLinkList from '@/components/ui/Table/TableLinkList'
import { mixinTableItem } from '@/mixins/table/mixinTableItem'

export default {
  name: 'TableTreeItem',
  components: {
    TableLinkList
  },
  mixins: [mixinTableItem],
  props: {
    lvl: {
      type: Number,
      required: false,
      default: 1
    },
    isOpen: {
      type: Boolean,
      required: false,
      default: true
    },
    visibleChild: {
      type: Boolean,
      required: false,
      default: true
    },
    countItem: {
      type: Number,
      required: false,
      default: 1
    },
    // для указания под какими ключами находятся категории и наполнение категорий
    childrenKeys: {
      type: Object,
      required: false,
      default: () => ({
        category: 'children_categories',
        categoryItem: 'positions'
      })
    },
    showActionColumn: {
      type: Boolean,
      default: false
    },
    // Для вывода кнопок по условия.
    // Например на первом уровне кнопки дожны выводиться всегда, а на втором только в случае раскрытия строки
    showActionsRule: {
      type: Function,
      default: () => true
    },
    // Весь элемент массива начиная с корня
    rootItem: {
      type: Object,
      required: false,
      default: null
    },
    // Родительский элемент для текущего
    parentItem: {
      type: Object,
      required: false,
      default: null
    },
    itemsLength: {
      type: Number,
      required: false,
      default: 0
    }
  },
  data: () => ({
    isOpenChild: true
  }),
  computed: {
    isCategory () {
      return this.currentLevelChildren.length > 0
    },

    categoryList () {
      if (this.item[this.childrenKeys.category]?.length) {
        return this.item[this.childrenKeys.category]
      }
      return []
    },

    categoryItemList () {
      if (this.item[this.childrenKeys.categoryItem]?.length) {
        return this.item[this.childrenKeys.categoryItem]
      }
      return []
    },

    classListRow () {
      const classes = []

      if (this.isCategory) {
        classes.push('table-tree__tr--category')
      }

      // Верхний уровень (lvl === 1) или промежуточные раскрытые строки
      if (this.lvl === 1 || this.isOpenChild) {
        classes.push('table-tree__tr--odd')
      }

      if (this.lvl === 1 && (this.itemsLength - 1 === this.$vnode.key) && !this.isOpenChild) {
        classes.push('border-inherit')
      }

      return classes
    },

    styleFirstTd () {
      const pixelAdd = this.isCategory ? 0 : 10
      return {
        'padding-left': 20 * this.lvl + pixelAdd + 'px'
      }
    },

    // Проверка соответствует ли строка условию для вывода кнопок
    canShowActions () {
      return this.showActionsRule({
        item: this.item,
        lvl: this.lvl,
        isCategory: this.isCategory,
        isOpenChild: this.isOpenChild
      })
    },

    // Определяем дочерние элементы для текущего уровня
    currentLevelChildren () {
      const children = []

      // Определяем ключи для текущего уровня
      const levelKeys = this.getLevelKeys()

      // Собираем все дочерние элементы для текущего уровня
      levelKeys.forEach(key => {
        if (this.item[key] && Array.isArray(this.item[key]) && this.item[key].length > 0) {
          this.item[key].forEach(child => {
            children.push({
              item: child,
              key: key,
              level: this.lvl + 1
            })
          })
        }
      })

      return children
    }
  },
  watch: {
    visibleChild: {
      handler (bool) {
        this.isOpenChild = bool
      },
      immediate: true
    }
  },
  methods: {
    openChilds () {
      this.isOpenChild = !this.isOpenChild
    },

    // Определяем ключи для текущего уровня
    getLevelKeys () {
      const { levelKeys, category, categoryItem } = this.childrenKeys

      // 1. Если определён levelKeys — используем его
      if (levelKeys) {
        return levelKeys[this.lvl] || []
      }

      // 2. Универсальная логика для бесконечной вложенности
      const keys = []

      if (Array.isArray(this.item[category]) && this.item[category].length > 0) {
        keys.push(category)
      }

      if (Array.isArray(this.item[categoryItem]) && this.item[categoryItem].length > 0) {
        keys.push(categoryItem)
      }

      return keys
    }
  }
}
</script>
